import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { SearchAction } from '../SpotlightState';

type Options = {
	refInput: React.RefObject<HTMLInputElement | null>;
	dispatch: React.Dispatch<SearchAction>;
};

export function useSearchByImage({ refInput, dispatch }: Options) {
	const [isSearchByImageOpen, setIsSearchByImageOpen] = useState(false);
	// Set only by the search-by-image back button — onSubmit closes Spotlight entirely,
	// so it shouldn't refocus an input that's about to be hidden.
	const shouldFocusInputOnCloseRef = useRef(false);

	const openSearchByImage = useCallback(() => setIsSearchByImageOpen(true), []);
	// Wrapped in `useCallback` so closeSpotlightDropdown, which is built from it, stays the same
	// function between renders.
	const closeSearchByImage = useCallback(() => setIsSearchByImageOpen(false), []);
	const returnFromSearchByImage = useCallback(() => {
		shouldFocusInputOnCloseRef.current = true;
		setIsSearchByImageOpen(false);
	}, []);

	useEffect(() => {
		if (!isSearchByImageOpen) {
			// SearchBar (and its input) only remounts once this effect runs, so focusing
			// here — not in the callback that flips isSearchByImageOpen — is required.
			if (shouldFocusInputOnCloseRef.current) {
				shouldFocusInputOnCloseRef.current = false;
				refInput.current?.focus();
			}

			return;
		}

		dispatch({ type: 'scopeReset' });
	}, [isSearchByImageOpen, refInput, dispatch]);

	return { isSearchByImageOpen, openSearchByImage, closeSearchByImage, returnFromSearchByImage };
}
